export type FnaQuestionType = "RADIO" | "CHECKBOX" | "INPUT";

const LEGACY_TO_CANONICAL: Record<string, string> = {
  additionalLifeCoverageAmountOption:
    "insurancePurchaseObjectives_A_ProposedInsured",
  additionalLifeCoverageAmountHKD:
    "insurancePurchaseObjectives_A_ProposedInsured_A_amount",
  additionalLifeCoverageAmountUSD:
    "insurancePurchaseObjectives_A_ProposedInsured_B_amount",
  insurancePurchaseMedicalObjectives: "insurancePurchaseObjectives_B",
  additionalCriticalIllnessCoverageAmountOption:
    "insurancePurchaseObjectives_B_C_ProposedInsured",
  additionalCriticalIllnessCoverageAmountHKD:
    "insurancePurchaseObjectives_B_C_ProposedInsured_A_amount",
  additionalCriticalIllnessCoverageAmountUSD:
    "insurancePurchaseObjectives_B_C_ProposedInsured_B_amount",
  investmentOptionManagementPreference: "insurancePurchaseObjectives_E",
  additionalTargetAmountOption:
    "insurancePurchaseObjectives_CDE_ProposedInsured",
  additionalTargetAmountHKD:
    "insurancePurchaseObjectives_CDE_ProposedInsured_A_amount",
  additionalTargetAmountUSD:
    "insurancePurchaseObjectives_CDE_ProposedInsured_B_amount",
  additionalTargetYears: "insurancePurchaseObjectives_CDE_year",
  insurancePurchaseOtherObjectiveOptions: "insurancePurchaseObjectives_F",
  insurancePurchaseOtherObjectives: "insurancePurchaseObjectives_F_E_remark",
  singlePremiumPaymentLimit: "willingPremiumPaymentPeriodRange_G_amount",
};

export const ZUU_FNA_CANONICAL_QUESTION_ORDER = [
  "insurancePurchaseObjectives",
  "insurancePurchaseObjectives_A_ProposedInsured",
  "insurancePurchaseObjectives_A_ProposedInsured_A_amount",
  "insurancePurchaseObjectives_A_ProposedInsured_B_amount",
  "insurancePurchaseObjectives_B",
  "insurancePurchaseObjectives_B_C_ProposedInsured",
  "insurancePurchaseObjectives_B_C_ProposedInsured_A_amount",
  "insurancePurchaseObjectives_B_C_ProposedInsured_B_amount",
  "insurancePurchaseObjectives_E",
  "insurancePurchaseObjectives_CDE_ProposedInsured",
  "insurancePurchaseObjectives_CDE_ProposedInsured_A_amount",
  "insurancePurchaseObjectives_CDE_ProposedInsured_B_amount",
  "insurancePurchaseObjectives_CDE_year",
  "insurancePurchaseObjectives_F",
  "insurancePurchaseObjectives_F_E_remark",
  "premiumToDisposableIncomeRatio",
  "premiumToLiquidAssetsRatio",
  "willingPremiumPaymentPeriodRange",
  "willingPremiumPaymentPeriodRange_G_amount",
  "policyTargetExpectedTime",
] as const;

const ZUU_FNA_CANONICAL_ORDER_INDEX = new Map(
  ZUU_FNA_CANONICAL_QUESTION_ORDER.map((question, index) => [question, index]),
);

const CANONICAL_TO_LEGACY = Object.fromEntries(
  Object.entries(LEGACY_TO_CANONICAL).map(([legacy, canonical]) => [
    canonical,
    legacy,
  ]),
) as Record<string, string>;

const LEGACY_CHECKBOX_TO_CANONICAL_RADIO = new Set([
  "insurancePurchaseMedicalObjectives",
  "insurancePurchaseOtherObjectiveOptions",
]);

function getSelectedKeys(question: any): string[] {
  return (question?.items || [])
    .filter((item: any) => item?.value === true)
    .map((item: any) => item?.key)
    .filter((key: any): key is string => Boolean(key));
}

function normalizeQuestionItems(question: any) {
  if (!Array.isArray(question?.items)) {
    return [];
  }

  if (question.type === "INPUT") {
    return question.items.filter((item: any) => item?.key);
  }

  return question.items.filter((item: any) => item?.key && item?.value === true);
}

function hasMeaningfulQuestionValue(question: any) {
  if (!question) return false;
  if (question.type === "INPUT") {
    return (question.items || []).some((item: any) => {
      const value = item?.value;
      return value !== undefined && value !== null && value !== "";
    });
  }

  return getSelectedKeys(question).length > 0;
}

export function getZuuFnaCanonicalQuestionKey(questionKey: string) {
  return LEGACY_TO_CANONICAL[questionKey] || questionKey;
}

export function getZuuFnaQuestionAliases(questionKey: string) {
  const canonicalKey = getZuuFnaCanonicalQuestionKey(questionKey);
  const legacyKey = CANONICAL_TO_LEGACY[canonicalKey];
  return Array.from(new Set([questionKey, canonicalKey, legacyKey].filter(Boolean)));
}

export function getZuuFnaQuestionValueFromList(
  list: any[],
  questionKey: string,
  type: FnaQuestionType = "RADIO",
) {
  const aliases = getZuuFnaQuestionAliases(questionKey);
  const questionObj = aliases
    .map((key) => list?.find((question) => question.question === key))
    .find((question) => hasMeaningfulQuestionValue(question)) ||
    aliases
      .map((key) => list?.find((question) => question.question === key))
      .find(Boolean);

  if (!questionObj) return undefined;

  if (type === "INPUT") {
    return questionObj.items?.find((item: any) => item.key === "A")?.value;
  }

  const selectedItems = getSelectedKeys(questionObj);
  if (type === "RADIO") {
    return selectedItems[0];
  }

  if (LEGACY_CHECKBOX_TO_CANONICAL_RADIO.has(questionKey)) {
    return selectedItems[0] ? [selectedItems[0]] : [];
  }

  return selectedItems;
}

export function normalizeZuuFnaQuestionList(list: any[] | undefined) {
  const normalizedList = (list || []).filter((question) => question?.question);
  const questionMap = new Map<string, any>();

  normalizedList.forEach((question) => {
    const canonicalKey = getZuuFnaCanonicalQuestionKey(question.question);
    let nextQuestion = {
      ...question,
      question: canonicalKey,
      items: normalizeQuestionItems(question),
    };

    if (LEGACY_CHECKBOX_TO_CANONICAL_RADIO.has(question.question)) {
      const selectedKey = getSelectedKeys(question)[0];
      nextQuestion = {
        ...nextQuestion,
        type: "RADIO",
        items: selectedKey ? [{ key: selectedKey, value: true }] : [],
      };
    }

    const existing = questionMap.get(canonicalKey);
    if (!existing || !hasMeaningfulQuestionValue(existing)) {
      questionMap.set(canonicalKey, nextQuestion);
    }
  });

  return Array.from(questionMap.values()).sort((a, b) => {
    const aIndex = ZUU_FNA_CANONICAL_ORDER_INDEX.get(a.question) ?? Number.MAX_SAFE_INTEGER;
    const bIndex = ZUU_FNA_CANONICAL_ORDER_INDEX.get(b.question) ?? Number.MAX_SAFE_INTEGER;
    if (aIndex !== bIndex) {
      return aIndex - bIndex;
    }
    return String(a.question).localeCompare(String(b.question));
  });
}

export function toZuuFnaStoredQuestionValue(
  questionKey: string,
  value: any,
  type: FnaQuestionType,
) {
  const canonicalKey = getZuuFnaCanonicalQuestionKey(questionKey);

  if (LEGACY_CHECKBOX_TO_CANONICAL_RADIO.has(questionKey) && type === "CHECKBOX") {
    const values = Array.isArray(value) ? value : [];
    return {
      canonicalKey,
      storedType: "RADIO" as const,
      storedValue: values[0],
    };
  }

  return {
    canonicalKey,
    storedType: type,
    storedValue: value,
  };
}
